// 플러그인별 서킷 브레이커 — whif-engine safeExec 의 CB 대응 (ADR-048 D5)
// 연속 실패 threshold 회 → OPEN(건너뜀) → openMs 경과 → HALF-OPEN(한 건만 시험) → 성공 CLOSED / 실패 OPEN
// 기본값은 엔진 값(5회·60초)을 시작점으로 옮긴 것이다 — 브라우저 적정성은 미측정 (prd/103 §5 측정 불가 항목)
//
// 실행 권한은 표(ticket)로 준다. 상태가 바뀔 때마다 세대(generation)가 오르고, 이전 세대 표로 돌아온
// 결과는 버린다 — 열리기 전에 출발한 호출이 늦게 실패해 차단 시간을 늘리거나, 늦게 성공해 닫아 버리는 것을 막는다.
export const DEFAULT_FAIL_THRESHOLD = 5;
export const DEFAULT_OPEN_MS = 60_000;
export const DEFAULT_HOOK_TIMEOUT_MS = 5_000;

export type BreakerState = 'closed' | 'open' | 'half-open';

export type BreakerTicket = number;

export interface BreakerOptions {
  readonly threshold?: number;
  readonly openMs?: number;
  readonly now?: () => number;
}

export class CircuitBreaker {
  private failures = 0;
  private openedAt: number | null = null;
  private generation = 0;
  private probing = false;
  private readonly threshold: number;
  private readonly openMs: number;
  private readonly now: () => number;

  constructor(options: BreakerOptions = {}) {
    this.threshold = options.threshold ?? DEFAULT_FAIL_THRESHOLD;
    this.openMs = options.openMs ?? DEFAULT_OPEN_MS;
    this.now = options.now ?? Date.now;
  }

  get state(): BreakerState {
    if (this.openedAt === null) return 'closed';
    return this.now() - this.openedAt >= this.openMs ? 'half-open' : 'open';
  }

  /** 실행 표를 받는다 — OPEN 이거나 반열림 시험이 이미 나가 있으면 null (실행하지 않는다) */
  acquire(): BreakerTicket | null {
    const state = this.state;
    if (state === 'open') return null;
    if (state === 'half-open') {
      if (this.probing) return null;
      this.probing = true;
    }
    return this.generation;
  }

  success(ticket: BreakerTicket): void {
    if (ticket !== this.generation) return;
    this.failures = 0;
    if (this.openedAt !== null) this.transition(null);
  }

  failure(ticket: BreakerTicket): void {
    if (ticket !== this.generation) return;
    if (this.openedAt !== null) {
      this.transition(this.now()); // 반열림 시험 실패 → 다시 열림
      return;
    }
    this.failures += 1;
    if (this.failures >= this.threshold) this.transition(this.now());
  }

  private transition(openedAt: number | null): void {
    this.openedAt = openedAt;
    this.probing = false;
    this.failures = 0;
    this.generation += 1;
  }
}
